import { SectionOneResponseDto } from '../dto/res/SectionOneResponseDto';
import {
  CitizenshipSelectionDto,
  CreateSectionOneRequestDto,
  EmployerSelectionDto,
  InterPreterOrPreparerDto,
  SignatureOfEmployeeDto,
} from '../dto/req/sectionOneRequestDto';
import { SectionOneForm } from '@/features/section-1/application/validators/section-one.schema';
import { SectionOneFormState } from '../../ui/view-models/useSectionOne';
import SectionOne from '@/features/section-1/application/domain/entities/SectionOne';
import { InterpreterSelectionEnum } from '@/features/section-1/features/interpreter/application/domain/entities/InterpreterSelection';
import { CitizenshipSelectionOptions } from '@/features/section-1/features/citizenship-selection/application/domain/entities/CitizenshipSelection';
import { mapInterpreterResponseDtoArrayToEntity } from '@/features/section-1/features/interpreter/infra/repositories/mappers/interpreterMapper';
import {
  SectionOneSteps,
  SectionOneStepValue,
} from '../../../application/domain/models/section-one-steps';

import { flattenPhoneNumber, formatDate } from '@/shared/infra/utils/utils';
import { toSection1ANumber } from '@/shared/application/utils/aNumber';

const section1ANumber = (value: string | undefined): string | undefined =>
  value ? toSection1ANumber(value) : value;

function getInterpreterStatus(isAssisted: boolean | undefined | null): string | undefined {
  if (isAssisted === undefined || isAssisted === null) {
    return undefined;
  }
  return isAssisted
    ? InterpreterSelectionEnum.GETTING_HELP_OF_INTERPRETER
    : InterpreterSelectionEnum.NO_INTERPRETER;
}

// Converts an ISO date string or Date object to MM/dd/yyyy, forcing UTC interpretation
// so midnight-UTC dates (e.g. "1993-06-15T00:00:00Z") are never shifted backward a day
// by negative-offset (US) local timezones on each save round-trip.
// Delegates to the shared formatDate utility which already uses { timeZone: 'UTC' }.
function formatDateToMMDDYYYY(isoDateString: string | Date): string {
  const date = typeof isoDateString === 'string' ? new Date(isoDateString) : isoDateString;
  return formatDate(date);
}

export function mapSectionOneDtoToEntity(dto: SectionOneResponseDto): SectionOne {
  // Helper: if value is empty string, return empty string, else undefined
  const keepEmptyString = (value: string | undefined): string | undefined =>
    value === '' ? '' : (value ?? undefined);

  return {
    formFields: {
      accountId: dto.salesForceAccountId,
      employerState: '',
      location: dto.locationId,
      lastName: dto.lastName,
      firstName: dto.firstName,
      middleInitial: keepEmptyString(dto.middleName),
      otherName: keepEmptyString(dto.otherName),
      address: dto.address,
      aptNumber: keepEmptyString(dto.appartmentNumber),
      city: dto.city,
      // A digitized paper I-9 can have no readable state; the form field is simply left unselected.
      state: dto.stateId ?? undefined,
      zipCode: dto.zipCode,
      dob: dto.dateOfBirth ? formatDateToMMDDYYYY(dto.dateOfBirth) : undefined,
      ssn: keepEmptyString(dto.ssn),
      confirmSsn: keepEmptyString(dto.ssn),
      doNotHaveSsn: dto.doNotHaveSsn,
      doNotHaveSSNOption: keepEmptyString(dto.doNotHaveSsnOption),
      email: keepEmptyString(dto.email),
      phone: keepEmptyString(dto.phoneNumber),
      citizenshipStatus: keepEmptyString(dto.citizenshipStatus) as CitizenshipSelectionOptions,
      uscisANumberOrAuthorizedAlienNumberForLawfulPermanentResident: keepEmptyString(
        dto.uscisANumberOrAuthorizedAlienNumberForLawfulPermanentResident
      ),
      uscisANumberOrAuthorizedAlienNumberForAlienAuthorizedToWork: keepEmptyString(
        dto.uscisANumberOrAuthorizedAlienNumberForAlienAuthorizedToWork
      ),
      authorizedI94Number: keepEmptyString(dto.formI94AdminssionNumber),
      foreignPassportNumber: keepEmptyString(dto.foreignPassportNumber),
      passportCountry: keepEmptyString(dto.countryOfIssuanceForPassport),
      interpreterStatus: getInterpreterStatus(dto.isAssistedByPreparorOrTranslator),
      interpreters: dto.supplementAFields
        ? mapInterpreterResponseDtoArrayToEntity(dto.supplementAFields)
        : undefined,
      signature: keepEmptyString(dto.signatureOfEmployee),
      expirationDate: dto.expirationDateForDocumentOfAlienAuthorizedToWork
        ? formatDateToMMDDYYYY(dto.expirationDateForDocumentOfAlienAuthorizedToWork)
        : null,
      // An older I-9 has no flag: it reads as no choice, so an amendment asks the employee afresh.
      workAuthorizationNoExpiration: dto.workAuthorizationNoExpiration === true,
      signatureDate: dto.dateOfSignature ? new Date(dto.dateOfSignature) : undefined,
    },
    status: dto.status as SectionOneFormState,
    i9formId: dto.i9FormId,
    currentStep: dto.section1CurrentStep
      ? mapStepValueToStep(dto.section1CurrentStep.currentStep)
      : SectionOneSteps.EmployerSelection,
    referenceNumber: dto.referenceNumber,
    remoteCompleterNamed: dto.remoteCompleterNamed === true,
    signedSection2OnFile: dto.signedSection2OnFile === true,
    locationName: dto.locationName ?? null,
  };
}

function getInterpreterStatusFromEnum(status: string | undefined): boolean | undefined {
  if (!status) {
    return undefined;
  }
  return status === InterpreterSelectionEnum.GETTING_HELP_OF_INTERPRETER ? true : false;
}

const stepValueToStepMap: Record<SectionOneStepValue, SectionOneSteps> = {
  [SectionOneStepValue.EmployerSelection]: SectionOneSteps.EmployerSelection,
  [SectionOneStepValue.CitizenshipSelection]: SectionOneSteps.CitizenshipSelection,
  [SectionOneStepValue.Interpreter]: SectionOneSteps.Interpreter,
  [SectionOneStepValue.EVerify]: SectionOneSteps.EVerify,
  [SectionOneStepValue.EmpDocTypeSelection]: SectionOneSteps.EmpDocTypeSelection,
  [SectionOneStepValue.EmpDocMetadataEntry]: SectionOneSteps.EmpDocMetadataEntry,
  [SectionOneStepValue.Signature]: SectionOneSteps.Signature,
  [SectionOneStepValue.Review]: SectionOneSteps.Review,
  [SectionOneStepValue.Confirmation]: SectionOneSteps.Confirmation,
};

export function mapStepValueToStep(stepValue: SectionOneStepValue): SectionOneSteps {
  const step = stepValueToStepMap[stepValue];
  if (step === undefined) {
    // if stepValue is not recognized, default to EmployerSelection
    return SectionOneSteps.EmployerSelection;
  }
  return step;
}

const stepToStepValueMap: Record<SectionOneSteps, SectionOneStepValue> = {
  [SectionOneSteps.EmployerSelection]: SectionOneStepValue.EmployerSelection,
  [SectionOneSteps.CitizenshipSelection]: SectionOneStepValue.CitizenshipSelection,
  [SectionOneSteps.Interpreter]: SectionOneStepValue.Interpreter,
  [SectionOneSteps.EVerify]: SectionOneStepValue.EVerify,
  [SectionOneSteps.EmpDocTypeSelection]: SectionOneStepValue.EmpDocTypeSelection,
  [SectionOneSteps.EmpDocMetadataEntry]: SectionOneStepValue.EmpDocMetadataEntry,
  [SectionOneSteps.Signature]: SectionOneStepValue.Signature,
  [SectionOneSteps.Review]: SectionOneStepValue.Review,
  [SectionOneSteps.Confirmation]: SectionOneStepValue.Confirmation,
  // Never persisted: the employee reaches CompleterEdit from their emailed link, and it does not
  // advance Section 1's saved progress. Mapped to Confirmation so the map stays exhaustive and a
  // stray save cannot rewind a finished form.
  [SectionOneSteps.CompleterEdit]: SectionOneStepValue.Confirmation,
};
export function mapStepToStepValue(step: SectionOneSteps): SectionOneStepValue {
  const stepValue = stepToStepValueMap[step];
  if (stepValue === undefined) {
    // if step is not recognized, default to EmployerSelection
    return SectionOneStepValue.EmployerSelection;
  }
  return stepValue;
}

export function mapSectionOneFormToDto(
  entity: SectionOneForm,
  status: SectionOneFormState
): CreateSectionOneRequestDto {
  const employerDto: EmployerSelectionDto = {
    salesForceAccountId: entity.accountId,
    employerState: entity.employerState,
    locationId: entity.location,
    lastName: entity.lastName,
    firstName: entity.firstName,
    middleInitial: entity.middleInitial,
    otherName: entity.otherName,
    address: entity.address,
    aptNumber: entity.aptNumber,
    city: entity.city,
    stateId: entity.state,
    zipCode: entity.zipCode,
    dateOfBirth: entity.dob || undefined,
    ssn: entity.ssn,
    doNotHaveSsn: entity.doNotHaveSsn ? true : false,
    doNotHaveSsnOption: entity.doNotHaveSSNOption,
    email: entity.email,
    phoneNumber: entity.phone ? flattenPhoneNumber(entity.phone) : undefined,
  };

  const citizenshipDto: CitizenshipSelectionDto = {
    cityzenshipStatus: entity.citizenshipStatus,
    // Sent as the server stores it (no spaces or hyphens, "A" uppercased), the rule both sides apply.
    uscisANumberOrAuthorizedAlienNumberForLawfulPermanentResident: section1ANumber(
      entity.uscisANumberOrAuthorizedAlienNumberForLawfulPermanentResident
    ),
    uscisANumberOrAuthorizedAlienNumberForAlienAuthorizedToWork: section1ANumber(
      entity.uscisANumberOrAuthorizedAlienNumberForAlienAuthorizedToWork
    ),
    formI94AdminssionNumber: entity.authorizedI94Number,
    foreignPassportNumber: entity.foreignPassportNumber,
    countryOfIssuanceForPassport: entity.passportCountry,
    expirationDateForDocumentOfAlienAuthorizedToWork: entity.expirationDate || null,
    workAuthorizationNoExpiration: entity.workAuthorizationNoExpiration === true,
  };

  const interpreterOrPreparerDto: InterPreterOrPreparerDto = {
    isAssistedByPreparorOrTranslator: getInterpreterStatusFromEnum(entity.interpreterStatus),
  };

  const signatureOfEmployeeDto: SignatureOfEmployeeDto = {
    signatureOfEmployee: entity.signature,
    dateOfSignature: entity.signatureDate
      ? entity.signatureDate instanceof Date
        ? entity.signatureDate.toISOString()
        : (entity.signatureDate as unknown as string)
      : undefined,
  };

  return {
    ...employerDto,
    ...citizenshipDto,
    ...interpreterOrPreparerDto,
    ...signatureOfEmployeeDto,
    status,
  };
}
